import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import BlogView from './BlogView'
import BlogList from './BlogList'
import BlogCategory from './BlogCategory'
import BlogEdit from './BlogEdit'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import helpers from 'utils/helpers'
import OSearch from 'components/reusable/OSearch'
import { statusOptions } from 'utils/constants'
import CustomSelect from 'components/reusable/CustomSelect'
import classNames from "classnames";

const tabBtnClass = "inline-block py-3 px-8 text-md xl:text-lg font-semibold border-b-2";
const tabActiveClass = "border-blue-600 text-blue-600";
const tabBorderNoneClass = "border-transparent";
const BlogManagement = () => {
  const { user, updatePageName } = useContext(AuthContext)
  const navigate = useNavigate()
  const manager =
    user?.permission?.find(e => e.manager === 'blog-management') ?? {}
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('blogs') // 'blogs' or 'categories'
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const [blogList, setBlogList] = useState([])
  const [categories, setCategories] = useState([])
  const [blogEdit, setBlogEdit] = useState(false)
  const [currentItem, setCurrentItem] = useState('')
  const [blogView, setBlogView] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [filterData, setFilterData] = useState({
    status: '',
    searchKey: '',
    startDate: '',
    endDate: '',
    isReset: false,
    isFilter: false
  })
  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc'
  })

  const handleEdit = item => {
    setCurrentItem(item)
    setBlogEdit(!blogEdit)
    navigate('/blog-management/edit', { state: item })
  }

  const handleView = item => {
    setCurrentItem(item)
    setBlogView(!blogView)
    navigate('/blog-management/view', { state: item })
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  useEffect(() => {
    updatePageName(t('NAV_BLOGS'))
  }, [])

  const getBlogs = async () => {
    try {
      setIsLoading(true)
      const { status, startDate, endDate, searchKey, isFilter } = filterData

      const payload = {
        page,
        pageSize,
        status: status,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format('YYYY-MM-DD'),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format('YYYY-MM-DD'),
          null
        ),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType
      }
      const path = apiPath.blog
      const result = await apiGet(path, payload)
      const responseData = result?.data?.results
      const resultStatus = result?.data?.success
      setBlogList(responseData?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, responseData.page, null),
        pageCount: helpers?.ternaryCondition(
          resultStatus,
          responseData.totalPages,
          null
        ),
        perPageItem: helpers?.ternaryCondition(
          resultStatus,
          responseData?.docs.length,
          null
        ),
        totalItems: helpers?.ternaryCondition(
          resultStatus,
          responseData.totalDocs,
          null
        )
      })
    } catch (error) {
      console.log('error in get all blog list==>>>>', error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const getCategories = async () => {
    try {
      const result = await apiGet(apiPath.blogCategory, { page: 1, pageSize: 100 });
      if (result?.data?.success) {
        setCategories(result.data.results.docs);
      }
    } catch (error) {
      console.log('Error fetching categories:', error);
    }
  }

  useEffect(() => {
    if (activeTab === 'blogs') {
      getBlogs();
    } else {
      getCategories();
    }
  }, [page, filterData, sort, activeTab])

  const handleReset = () => {
    setFilterData({
      status: '',
      searchKey: '',
      endDate: '',
      startDate: '',
      isReset: true,
      isFilter: false
    })
    setPage(1)
    setSearchTerm('')
  }

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true
    })
  }

  const statusPage = e => {
    setFilterData({ ...filterData, status: e.target.value, isFilter: true })
    setPage(1)
  }

  return (
    <>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            {/* Tabs */}
            <div className="py-3 my-2">
                <ul className="border-b flex">
                    <li className="mr-0 xl:mr-5">
                    <button
                        onClick={() => setActiveTab('blogs')}
                        className={classNames({
                        [tabBtnClass]: true,
                        [tabActiveClass]: activeTab === 'blogs',
                        [tabBorderNoneClass]: activeTab !== 'blogs',
                        })}
                    >
                        {t('BLOGS')}
                    </button>
                    </li>
                    <li className="mx-0 xl:mx-5">
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={classNames({
                        [tabBtnClass]: true,
                        [tabActiveClass]: activeTab === 'categories',
                        [tabBorderNoneClass]: activeTab !== 'categories',
                        })}
                    >
                        {t('CATEGORIES')}
                    </button>
                    </li>
                </ul>
            </div>
            {activeTab === 'blogs' && (
              <>
                <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
                  <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                    <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                      <div className='relative flex items-center mb-3'>
                        <OSearch
                          searchTerm={searchTerm}
                          setSearchTerm={setSearchTerm}
                          placeholder={t('SEARCH_BY_TITLE')}
                        />
                      </div>
                      <ODateRangePicker
                        handleDateChange={handleDateChange}
                        isReset={filterData?.isReset}
                        setIsReset={setFilterData}
                      />
                      <CustomSelect
                        id='status'
                        name='status'
                        value={filterData.status}
                        onChange={statusPage}
                        options={statusOptions}
                        placeholder='O_ALL'
                        t={t}
                      />
                      <OButton
                        label='Reset'
                        type='button'
                        onClick={handleReset}
                        extraClasses='ml-3 mb-3'
                        style={{ borderRadius: '0.5rem' }}
                      />
                    </div>
                  </div>
                  <div className='flex items-center justify-end px-4 ms-auto mb-3'>
                    {(manager?.add || user?.role === 'admin') && (
                      <button
                        title='Add blog'
                        type='button'
                        className='bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap'
                        onClick={() => navigate('/blog-management/add')}
                      >
                        + {t('ADD_BLOG')}
                      </button>
                    )}
                  </div>
                </form>
                
                <BlogList
                  blogList={blogList}
                  getBlogs={getBlogs}
                  page={page}
                  handleEdit={handleEdit}
                  handleView={handleView}
                  currentItem={currentItem}
                  blogView={blogView}
                  setSort={setSort}
                  sort={sort}
                  manager={manager}
                  isLoading={isLoading}
                />

                {blogView && (
                  <BlogView
                    blogView={blogView}
                    currentItem={currentItem}
                    handleView={handleView}
                  />
                )}

                {blogEdit && (
                  <BlogEdit
                    handleEdit={handleEdit}
                    currentItem={currentItem}
                    getBlogs={getBlogs}
                  />
                )}
                
                {paginationObj?.totalItems ? (
                  <Pagination
                    handlePageClick={handlePageClick}
                    options={paginationObj}
                    page={page}
                  />
                ) : (null)}
              </>
            )}

            {activeTab === 'categories' && (
                <BlogCategory
                    categories={categories}
                    getCategories={getCategories}
                    manager={manager}
                    user={user}
                    page={page}
                    isLoading={isLoading}
                />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default BlogManagement